import React from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import KeyStatistics from '../components/KeyStatistics';
import WhoWeAreSection from '../components/WhoWeAreSection';
import LeadershipSection from '../components/LeadershipSection';
import MarketPresenceSection from '../components/MarketPresence';
import ClientTestimonial from '../components/ClientTestimonial';
import TmCards from '../components/TmCards';

const Home = () => {
  const heroCtas = [
    { label: 'TALK TO US', to: '/contact' },
  ];

  const markets = [
    {
      label: 'Healthcare',
      path: 'M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z' // Heart
    },
    {
      label: 'Education',
      path: 'M22 10v6M2 10l10-5 10 5-10 5zM6 12v5c3 3 9 3 12 0v-5' // Graduation Cap
    },
    {
      label: 'Commercial',
      path: 'M3 21h18M5 21V7l8-4 8 4v14M8 21V11l6-3 6 3v10' // Building
    },
    {
      label: 'Sports',
      path: 'M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z' // Generic Sport
    },
    {
      label: 'Aviation',
      path: 'M2 12h20M15 5l7 7-7 7' // Flight/Arrow direction
    },
    {
      label: 'Pharmaceutical',
      path: 'M10.5 20.5l10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z' // Pill
    },
    {
      label: 'Retail',
      path: 'M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z M3 6h18 M16 10a4 4 0 0 1-8 0' // Shopping Bag
    },
    {
      label: 'Green Building',
      path: 'M12 2.5a5.5 5.5 0 0 1 5.5 5.5c0 3.5-3.5 11-5.5 11S6.5 11.5 6.5 8a5.5 5.5 0 0 1 5.5-5.5z' // Leaf
    },
    {
      label: 'Civic & Cultural',
      path: 'M4 22h16M4 10h16M2 10h20M6 10V22M18 10V22M10 10V22M14 10V22M12 2L2 7v3h20V7z' // Classical Building
    },
];

  return (
    <div className="w-full bg-white" style={{ fontFamily: "Monstserrat, proxima-nova, Helveti, sans-serif" }}>
      {/* Hero with video background */}
      <section className="relative h-screen min-h-[640px] flex items-center overflow-hidden -mt-20">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src='/images/TM_vedio.mp4' type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-black/45 z-10" />

        <div className="min-h-screen mx-auto px-4 sm:px-6 lg:px-12 relative z-20 grid place-items-center">
          <AnimatedSection animationType="fadeUp">
            <div className="text-center max-w-5xl space-y-10 flex flex-col items-center">

              {/* ✅ Typing Animated Text (NO line-clamp, NO dots) */}
              <p className="inline-block text-4xl md:text-5xl text-gray-100 font-semibold max-w-9xl mx-auto leading-tight">
                TM International Group
              </p>
              <p className="text-3xl md:text-4xl text-gray-100 font-light leading-tight" style={{ margin: 0 }}>
                Contractors You Can Trust
              </p>

              {/* ✅ Stagger Fade-in Buttons */}
              <motion.div
                className="grid grid-cols-1 gap-5 place-items-center justify-center"
                initial="hidden"
                animate="visible"
                variants={{
                  visible: {
                    transition: { staggerChildren: 0.2 },
                  },
                }}
              >
                {heroCtas.map((cta) => (
                  <motion.button
                    key={cta.label}
                    variants={{
                      hidden: { opacity: 0, y: 30 },
                      visible: { opacity: 1, y: 0 },
                    }}
                    className="w-fit bg-transparent text-white px-[24px] py-[8px] uppercase tracking-wide font-bold 
                      hover:bg-white hover:text-black transition-colors duration-300 flex items-center justify-center gap-2 group"
                    style={{ fontSize: "15px", border: "3px solid white" }}
                    onClick={() => {
                      window.location.href = "contact";
                    }}
                  >
                    {cta.label}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                ))}
              </motion.div>

            </div>
          </AnimatedSection>
        </div>
      </section>
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">

      {/* Our Story */}
      <KeyStatistics />

      {/* Who we are */}
      <WhoWeAreSection />

      {/* Services grid */}
      {/* <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <AnimatedSection animationType="fadeUp">
            <div className="text-center mb-14 space-y-4">
              <p className="text-sm uppercase tracking-[0.25em] text-[#1b2a5a]">Our Services</p>
              <h2 className="text-4xl md:text-5xl font-semibold text-[#111827]">How we deliver</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our expertise and value-added offerings support clients throughout the life-span of
                every construction process.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <AnimatedSection
                key={service.title}
                animationType="fadeUp"
                delay={index * 80}
                className="h-full"
              >
                <div className="bg-white h-full p-8 rounded-xl shadow-md border border-gray-100 hover:-translate-y-1 hover:shadow-xl transition">
                  <div className="mb-6">{service.icon}</div>
                  <h3 className="text-2xl font-bold text-[#111827] mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <Link
                    to="/what-we-do"
                    className="text-[#1b2a5a] font-semibold inline-flex items-center group"
                  >
                    Learn more
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section> */}
      <TmCards />

      {/* Leadership section */}
      <LeadershipSection />

      {/* Markets grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
            
            {/* Left Text Content */}
            <AnimatedSection animationType="fadeUp">
              <div className="space-y-4 w-full">
                <motion.h2 
                  initial={{ opacity: 0, y: -20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-2xl sm:text-3xl md:text-4xl text-left font-semibold text-black"
                >
                  Building across sectors
                </motion.h2>
                <p className="text-gray-600 leading-relaxed">
                  As one of the largest general contractors in the country, we lead in all major
                  market segments, from healthcare and aviation to retail and green building.
                </p>
                
                <Link
                  to="/portfolio-summary"
                  className="inline-flex items-center text-[#1b2a5a] font-semibold hover:text-[#0f1b3a] group mt-2"
                >
                  See our portfolio
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </AnimatedSection>

            {/* Right Grid Content */}
            <AnimatedSection className="lg:col-span-2">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {markets.map((market) => (
                  <div
                  key={market.label}
                  className="bg-gray-50 hover:bg-white border border-gray-100 hover:border-gray-200 py-6 flex flex-col items-center justify-center text-center transition-all duration-300 group cursor-default"
                  >
                  {/* Icon Container */}
                  <div className="mb-4 p-3 bg-white rounded-full shadow-sm text-[#1b2a5a] group-hover:bg-[#1b2a5a] group-hover:text-white transition-colors duration-300">
                      <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="24" 
                      height="24" 
                      // viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      >
                      <path d={market.path} />
                      </svg>
                  </div>
                  
                  {/* Label */}
                  <span className="font-semibold text-[#111827] group-hover:text-[#1b2a5a] transition-colors">
                      {market.label}
                  </span>
                  </div>
              ))}
              </div>
          </AnimatedSection>

          </div>
        </div>
      </section>

      {/* Market Presence */}
      <MarketPresenceSection />

      {/* Featured project */}
      {/* <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <AnimatedSection animationType="fadeUp">
              <div className="space-y-4">
                <p className="text-sm uppercase tracking-[0.25em] text-[#1b2a5a]">Featured Project</p>
                <h2 className="text-4xl font-bold text-[#111827]">{featuredProject.title}</h2>
                <p className="text-gray-600">{featuredProject.description}</p>
                <Link
                  to="/our-work"
                  className="inline-flex items-center text-[#1b2a5a] font-semibold hover:text-[#0f1b3a] group"
                >
                  View all projects
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection animationType="fadeUp" delay={120}>
              <div className="relative h-[320px] bg-gradient-to-br from-[#1b2a5a] via-[#213366] to-[#0a1533] rounded-2xl shadow-2xl overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_30%),radial-gradient(circle_at_80%_30%,rgba(255,255,255,0.12),transparent_25%)]" />
                <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-sm rounded-xl p-5 shadow-lg flex items-start gap-3">
                  <Leaf className="w-10 h-10 text-[#1b2a5a]" />
                  <div>
                    <h4 className="text-lg font-semibold text-[#111827]">Sustainable by design</h4>
                    <p className="text-sm text-gray-600">
                      Offsite manufacturing, resilient facades, and healthy materials for a modern
                      guest experience.
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section> */}

      {/* Client Testimonial */}
      <ClientTestimonial />

      {/* Horizontal Image Carousel */}
      {/* <HorizontalImageCarousel /> */}

      {/* Careers CTA */}
      {/* <section className="py-20 bg-[#1b2a5a] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <AnimatedSection animationType="fadeUp">
              <div className="space-y-4">
                <p className="text-sm uppercase tracking-[0.25em] text-gray-200">Careers</p>
                <h2 className="text-4xl md:text-5xl font-bold">Join the team</h2>
                <p className="text-lg text-gray-200">
                  Work with ambitious people on impactful projects in diverse locations. Opportunities
                  await for engineers, trade partners, veterans, and students.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/careers"
                    className="inline-flex items-center px-6 py-3 bg-white text-[#1b2a5a] font-semibold rounded-lg hover:bg-gray-100 transition"
                  >
                    Explore careers
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center px-6 py-3 border border-white/70 text-white font-semibold rounded-lg hover:bg-white/10 transition"
                  >
                    Talk to us
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animationType="fadeUp" delay={120}>
              <div className="bg-white/10 border border-white/20 rounded-2xl p-6 grid grid-cols-2 gap-4">
                {[
                  { label: 'Students & Entry Level', icon: <Rocket className="w-6 h-6" /> },
                  { label: 'Experienced Professionals', icon: <Briefcase className="w-6 h-6" /> },
                  { label: 'Trade & Labor', icon: <HardHat className="w-6 h-6" /> },
                  { label: 'Military Veterans', icon: <ShieldCheck className="w-6 h-6" /> },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 text-white"
                  >
                    {item.icon}
                    <span className="text-sm font-semibold">{item.label}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section> */}
      </div>
    </div>
  );
};

export default Home;